<script lang="ts">
	import { Slider, CardGrid, SelectCard } from '@Shelly/shelly-design-system';
	import { createEventDispatcher } from 'svelte';
	import { capFirstChar } from '../../../../../../../../../helpers/utilities';
	import { format } from 'svelte-i18n';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	export let actions = []; //both options - select percent level or change roller/cover state
	export let active_actions = []; // active action must be the one of both provided options
	export let isRollerCalibrated = true;
	export let mode = 'add';
	export let sceneIsValid; // Do not remove this line, it is used in the parent component and its validation

	const dispatch = createEventDispatcher();

	const POSITION_ACTION_NAMES = ['roller position', 'multi_cover_position', 'position'];
	const TILT_ACTION_NAMES = ['blinds_tilt_position', 'multi_cover_blinds_tilt_position'];
	const isTiltActionName = (name?: string) => !!name && TILT_ACTION_NAMES.includes(name);
	const isPositionActionName = (name?: string) => !!name && POSITION_ACTION_NAMES.includes(name);
	const mappedActionIcons = {
		0: 'fal fa-blinds-raised',
		1: 'fal fa-hand',
		2: 'fal fa-blinds',
		3: 'fal fa-blinds',
	};
	const tiltCardIcon = 'fal fa-arrows-up-down-left-right';
	const a11yProps = {
		ariaLabel: $format('label_adjust_value', { default: 'Adjust value' }),
		ariaLabelValue: null,
	};
	const a11yPropsTilt = {
		ariaLabel: $format('label_blinds_tilt', { default: 'Blinds tilt' }),
		ariaLabelValue: null,
	};

	let positionSliderValue =
		typeof active_actions[0]?.value === 'number' && !isTiltActionName(active_actions[0]?.name)
			? active_actions[0].value
			: 0;
	let tiltSliderValue =
		typeof active_actions[0]?.value === 'number' && isTiltActionName(active_actions[0]?.name)
			? active_actions[0].value
			: 0;
	let positionInitialValue = positionSliderValue;
	let tiltInitialValue = tiltSliderValue;
	let showSliderPosition =
		typeof active_actions[0]?.value === 'number' && !isTiltActionName(active_actions[0]?.name);
	let showSliderTilt = isTiltActionName(active_actions[0]?.name);

	$: tiltAction = actions[2];
	$: selected_state = active_actions?.[0]?.value || undefined;

	const change_active_act = (act, val) => {
		const isTiltSelected = isTiltActionName(active_actions[0]?.name);
		const isPositionSelected = typeof active_actions[0]?.value === 'number' && !isTiltSelected;
		let doNotDispatch = val === 'position' && isPositionSelected;

		if (!doNotDispatch) {
			const sliderValue = act.id === 'roller position' ? positionSliderValue : val;

			showSliderPosition = sliderValue === 'position';
			showSliderTilt = false;

			active_actions[0] = {
				name: act.id,
				value: sliderValue,
			};

			dispatch('change_active', { new_active: active_actions });
		}

		if (val === 'position' && positionInitialValue !== 0) {
			positionSliderValue = typeof positionInitialValue !== 'undefined' ? positionInitialValue : 0;

			active_actions[0] = {
				name: 'position',
				value: positionSliderValue,
			};

			dispatch('change_active', { new_active: active_actions });
		}
	};

	const select_tilt_action = () => {
		if (!tiltAction) return;
		showSliderPosition = false;
		showSliderTilt = true;

		active_actions[0] = {
			name: tiltAction.id,
			value: tiltSliderValue,
		};

		dispatch('change_active', { new_active: active_actions });
	};

	const handle_slider_changes = (e, act) => {
		positionSliderValue = e.detail.value;
		positionInitialValue =
			positionSliderValue !== positionInitialValue ? positionSliderValue : positionInitialValue;

		active_actions[0] = {
			name: act?.id || 'roller position',
			value: positionSliderValue,
		};
		dispatch('change_active', { new_active: active_actions });
	};

	const handle_tilt_slider_changes = (e) => {
		if (!tiltAction) return;
		tiltSliderValue = e.detail.value;
		tiltInitialValue = tiltSliderValue !== tiltInitialValue ? tiltSliderValue : tiltInitialValue;

		active_actions[0] = {
			name: tiltAction.id,
			value: tiltSliderValue,
		};
		dispatch('change_active', { new_active: active_actions });
	};

	const _prepare_msg_by_id = (id, _val) => {
		if (id === 'opening_state') {
			return $format('scene_action_device_state', {
				values: { state: $format(`execute_${_val}`) },
			});
		} else {
			return $format('scene_action_roller_state', {
				values: { roller_state: $format(`execute_${_val}`) },
			});
		}
	};

	if (mode === 'edit' && isPositionActionName(active_actions[0]?.name) && !isRollerCalibrated) {
		change_active_act(actions[0], actions[0]?.values[0]);
	}
</script>

<!-- active_actions[0] returns the state options - 'open', 'stop', 'close', 'position'. -->
<!-- active_actions[1] returns the position option - 'multi_cover_position', 'roller position', 'position'. -->
<!-- active_actions[2] returns the blinds tilt option - 'blinds_tilt_position' or 'multi_cover_blinds_tilt_position' (only for cover devices with tilt enabled). -->
{#if actions.length > 0}
	<CardGrid sm={200} md={200} xl={200} noPaddingBottom class="mb-l">
		{#each actions[0]?.values as _val, _i}
			<div class="d-flex">
				<SelectCard
					cardBgColor={$showNewLayout ? 'color-1' : undefined}
					class="width-full"
					type="horizontal"
					variant="radio"
					title={`${_prepare_msg_by_id(actions[0].id, _val)}`}
					icon={mappedActionIcons[_i]}
					selected={(selected_state === _val && !isTiltActionName(active_actions[0]?.name)) ||
						(_i === 3 &&
							isPositionActionName(active_actions[0]?.name) &&
							typeof active_actions[0]?.value === 'number')}
					on:click={() => {
						change_active_act(actions[0], _val);
					}}
					disabled={_val === 'position' && !isRollerCalibrated}
				/>
			</div>
		{/each}
		{#if tiltAction}
			<div class="d-flex">
				<SelectCard
					cardBgColor={$showNewLayout ? 'color-1' : undefined}
					class="width-full"
					type="horizontal"
					variant="radio"
					title={$format('scene_action_blinds_tilt_state', {
						default: 'set blinds tilt Shelly roller',
					})}
					icon={tiltCardIcon}
					selected={isTiltActionName(active_actions[0]?.name)}
					on:click={select_tilt_action}
					disabled={!isRollerCalibrated}
				/>
			</div>
		{/if}
	</CardGrid>
	{#if showSliderPosition}
		<Slider
			id="slider-v07tc80"
			{a11yProps}
			value={positionSliderValue}
			type="simple"
			min={0}
			max={100}
			title={capFirstChar($format(actions[1].title))}
			on:value_change={(e) => handle_slider_changes(e, actions[1])}
		/>
	{/if}
	{#if showSliderTilt && tiltAction}
		<Slider
			id="slider-blinds-tilt"
			a11yProps={a11yPropsTilt}
			value={tiltSliderValue}
			type="simple"
			min={0}
			max={100}
			title={capFirstChar($format(tiltAction.title))}
			on:value_change={handle_tilt_slider_changes}
		/>
	{/if}
{/if}
